import { Box } from '@mui/material'
import { ReactNode, useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Colors } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

import { useShouldShowOnboardingBanner } from '../../hooks/useShouldShowOnboardingBanner'
import { OnboardingChecklist } from '../../models/onboarding'
import { OnboardingBanner } from '../../pages/Onboarding/OnboardingBanner'
import { isVendorUserSelector } from '../../redux/selectors/users'
import { useLayoutContext } from '../Context/LayoutContext'
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress'
import { ContentGrid, PageContainer } from './Container'
import { AppCompanyBrand } from './Navbar/AppCompanyBrand'
import { Navbar } from './Navbar/NavBar'
import { SideBar } from './Navbar/SideBar'
import { useSidebarWidth } from './Navbar/useSideBarWidth'
import { VendorNav } from './Navbar/VendorNav'

interface LayoutProps {
  children: ReactNode
  checklist: readonly OnboardingChecklist[] | null
}

export const NAV_HEIGHT = 64

export const Layout = ({ children, checklist }: LayoutProps) => {
  const isVendorUser = useSelector(isVendorUserSelector)
  const shouldShowOnboardingBanner = useShouldShowOnboardingBanner(checklist)
  const shouldHideAllUI = window.location.pathname.includes('mobile-auth')
  const { toolbar, fixNavToTop, toolbarHeight, recalculateToolbarHeight, isFlowPage } = useLayoutContext()
  const sidebarWidth = useSidebarWidth()
  const [navbar, setNavbar] = useState<HTMLDivElement | null>(null)
  const [navHeight, setNavHeight] = useState(NAV_HEIGHT)

  useLayoutEffect(() => {
    if (!navbar) return
    const measure = () => setNavHeight(Math.ceil(navbar.getBoundingClientRect().height) || NAV_HEIGHT)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(navbar)
    return () => observer.disconnect()
  }, [navbar])

  useLayoutEffect(() => {
    const toolbarContent = toolbar.current?.parentElement
    if (!toolbarContent) return

    recalculateToolbarHeight()
    const observer = new ResizeObserver(recalculateToolbarHeight)
    observer.observe(toolbarContent)
    return () => observer.disconnect()
  }, [toolbar, recalculateToolbarHeight, shouldHideAllUI])

  if (shouldHideAllUI) {
    return (
      <ContentGrid>
        <div className="py-4">
          <AppCompanyBrand />
        </div>
        {children}
      </ContentGrid>
    )
  }

  return (
    <PageContainer
      sideBar={<SideBar />}
      toolbar={
        <>
          <NavbarWrap
            ref={setNavbar}
            className={fixNavToTop ? 'mui-fixed app-nav-wrap' : 'app-nav-wrap'} // This class will make that when any Dialog is open, the padding that is added to the body is also added to fixed elements
            $fixNavToTop={fixNavToTop}
            $sidebarWidth={sidebarWidth}
          >
            <ContentGrid noMaxWidth>{isVendorUser ? <VendorNav /> : <Navbar />}</ContentGrid>
          </NavbarWrap>

          <ToolbarWrap
            style={{ top: fixNavToTop ? navHeight : 0 }}
            className={fixNavToTop ? 'mui-fixed' : ''}
            $fixNavToTop={fixNavToTop}
            $sidebarWidth={sidebarWidth}
          >
            <ContentGrid isFlowPage={isFlowPage}>
              {shouldShowOnboardingBanner && fixNavToTop && <OnboardingBanner checklist={checklist} />}
              <div ref={toolbar} />
            </ContentGrid>
          </ToolbarWrap>
        </>
      }
    >
      {fixNavToTop && <div style={{ paddingTop: navHeight }} />}
      {toolbar.current && <div style={{ paddingTop: toolbarHeight }} />}
      {shouldShowOnboardingBanner && !fixNavToTop && <OnboardingBanner checklist={checklist} />}
      <OnboardingProgress />
      <Box mt={toolbarHeight ? 0 : 2} />
      {isVendorUser && <Box mt={7} />}
      {children}
      <Box mt={5} />
    </PageContainer>
  )
}

const GenericWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`

const NavbarWrap = styled(GenericWrap)<{ $fixNavToTop: boolean; $sidebarWidth: number }>`
  grid-area: navbar;
  z-index: 4;
  background: ${Colors.white};
  border-bottom: 1px solid ${Colors.lightGray4};
  box-sizing: border-box;
  top: 0;

  ${(p) =>
    p.$fixNavToTop &&
    css`
      position: fixed;
      left: ${p.$sidebarWidth}px;
      right: 0;
      width: calc(100% - ${p.$sidebarWidth}px);
    `};
`

/*
  Toolbar is a fix element usually below the nav
  It can be either fixed when nav is fixed
  Or it can be sticky, when nav is static so it become fixed to the top on the scroll.

*/
const ToolbarWrap = styled(GenericWrap)<{ $fixNavToTop: boolean; $sidebarWidth: number }>`
  grid-area: toolbar;
  position: fixed;
  left: 0;
  right: 0;
  background: ${Colors.bgGray};
  z-index: 3;

  ${(p) =>
    p.$fixNavToTop &&
    css`
      left: ${p.$sidebarWidth}px;
      width: calc(100% - ${p.$sidebarWidth}px);
    `};

  ${(p) =>
    !p.$fixNavToTop &&
    css`
      position: sticky;
    `};
`
